import type { ClientOverviewBootstrapDTO } from "../models/clientOverviewBootstrapDto";
import type {
  BootstrapSectionEntity,
  ClientOverviewBootstrapEntity,
} from "../../domain/entities/ClientOverviewBootstrapEntity";
import { monthAtGlanceDtoToEntity } from "./monthAtGlanceMapper";
import { needsYourAttentionDtoToEntity } from "./needsYourAttentionMapper";
import { thisMonthInProgressDtoToEntity } from "./thisMonthInProgressMapper";
import { weeklyBreakdownDtoToEntity } from "./weeklyBreakdownMapper";
import { financialPositionDtoToEntity } from "./financialPositionMapper";
import { twelveMonthTrendDtoToEntity } from "./twelveMonthTrendMapper";
import { arApAgingDtoToEntity } from "./arApAgingMapper";

function mapBootstrapSection<TDto, TEntity>(
  section: { status?: "ok" | "error"; data?: TDto | null; error?: string | null } | undefined,
  mapper: (dto: TDto) => TEntity,
): BootstrapSectionEntity<TEntity> {
  if (!section || section.status !== "ok" || section.data == null) {
    return {
      status: "error",
      data: null,
      error: section?.error ?? "Section unavailable",
    };
  }

  return {
    status: "ok",
    data: mapper(section.data),
    error: null,
  };
}

export function clientOverviewBootstrapDtoToEntity(
  dto: ClientOverviewBootstrapDTO,
): ClientOverviewBootstrapEntity {
  const sections = dto.sections ?? ({} as ClientOverviewBootstrapDTO["sections"]);

  return {
    sections: {
      monthAtGlance: mapBootstrapSection(
        sections.monthAtGlance,
        monthAtGlanceDtoToEntity,
      ),
      needsYourAttention: mapBootstrapSection(
        sections.needsYourAttention,
        needsYourAttentionDtoToEntity,
      ),
      thisMonthInProgress: mapBootstrapSection(
        sections.thisMonthInProgress,
        thisMonthInProgressDtoToEntity,
      ),
      ...(sections.weeklyBreakdown !== undefined
        ? {
            weeklyBreakdown: mapBootstrapSection(
              sections.weeklyBreakdown,
              weeklyBreakdownDtoToEntity,
            ),
          }
        : {}),
      financialPosition: mapBootstrapSection(
        sections.financialPosition,
        financialPositionDtoToEntity,
      ),
      twelveMonthTrend: mapBootstrapSection(
        sections.twelveMonthTrend,
        twelveMonthTrendDtoToEntity,
      ),
      arApAging: mapBootstrapSection(
        sections.arApAging,
        arApAgingDtoToEntity,
      ),
    },
    meta: {
      partial: dto.meta?.partial ?? false,
    },
  };
}
